import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type {
  AuditLogFilterOptionsResponse,
  AuditLogsResponse,
} from "@/app/audit-logs/types";
import {
  type DateRangeValue,
  dateRangeToStartTime,
} from "@/app/audit-logs/utils/date-range";
import type {
  AuditAction,
  AuditSource,
  ResourceType,
} from "@/app/audit-logs/utils/enum-helpers";
import { getCoordinatorApi } from "@/lib/api";
import { auditLogsKeys } from "./query-keys";

type FetchAuditLogsParams = {
  organizationId: string;
  projectId?: string;
  actions?: AuditAction[];
  sources?: AuditSource[];
  resourceTypes?: ResourceType[];
  startTime?: string;
  cursor?: string;
  limit?: number;
};

export type InfiniteAuditLogsParams = Omit<
  FetchAuditLogsParams,
  "cursor" | "limit" | "startTime"
> & {
  dateRange?: DateRangeValue;
};

export const fetchAuditLogs = async ({
  organizationId,
  projectId,
  actions,
  sources,
  resourceTypes,
  startTime,
  cursor,
  limit = 10,
}: FetchAuditLogsParams): Promise<AuditLogsResponse> => {
  const api = getCoordinatorApi(
    projectId ? { organizationId, projectId } : { organizationId }
  );
  const { data } = await api.get<AuditLogsResponse>("/audit_logs", {
    params: {
      limit,
      action: actions,
      source: sources,
      resource_type: resourceTypes,
      start_time: startTime ? startTime.replace("Z", "+00:00") : undefined,
      cursor,
    },
  });

  return data;
};

export const auditLogsQueryOptions = (params: InfiniteAuditLogsParams) => {
  const { dateRange, ...rest } = params;
  const startTime = dateRange ? dateRangeToStartTime(dateRange) : undefined;

  return infiniteQueryOptions<AuditLogsResponse>({
    queryKey: auditLogsKeys.list(params),
    queryFn: ({ pageParam }) =>
      fetchAuditLogs({
        ...rest,
        startTime,
        cursor: pageParam as string | undefined,
      }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) =>
      lastPage.data.has_more ? lastPage.data.next_cursor : undefined,
  });
};

export type FilterOptionsParams = {
  organizationId: string;
  projectId?: string;
  actions?: AuditAction[];
  sources?: AuditSource[];
  resourceTypes?: ResourceType[];
  dateRange?: DateRangeValue;
};

export const fetchAuditLogFilterOptions = async ({
  organizationId,
  projectId,
  actions,
  sources,
  resourceTypes,
  startTime,
}: Omit<FilterOptionsParams, "dateRange"> & {
  startTime?: string;
}): Promise<AuditLogFilterOptionsResponse> => {
  const api = getCoordinatorApi(
    projectId ? { organizationId, projectId } : { organizationId }
  );
  const { data } = await api.get<AuditLogFilterOptionsResponse>(
    "/audit_logs/filter_options",
    {
      params: {
        action: actions,
        source: sources,
        resource_type: resourceTypes,
        start_time: startTime ? startTime.replace("Z", "+00:00") : undefined,
      },
    }
  );

  return data;
};

export const auditLogFilterOptionsQueryOptions = (
  params: FilterOptionsParams
) => {
  const { dateRange, ...rest } = params;
  const startTime = dateRange ? dateRangeToStartTime(dateRange) : undefined;

  return queryOptions<AuditLogFilterOptionsResponse>({
    queryKey: auditLogsKeys.filterOptions(params),
    queryFn: () =>
      fetchAuditLogFilterOptions({
        ...rest,
        startTime,
      }),
  });
};
